import { mutatorAction } from 'satcheljs';
import { getFreeBusyCacheStore } from '../store/store';
import type FreeBusyRow from '../schema/FreeBusyRow';
import { getAttendeeData, getAttendeeSessions } from '../selectors/attendeeSelectors';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import type WorkingHoursType from 'owa-service/lib/contract/WorkingHoursType';
import type { GetScheduleFlexibleWorkingHoursView } from 'owa-graph-schema';
import mergeFlexibleWorkingHours from '../utils/mergeFlexibleWorkingHours';
import { getSessionAttendees } from '../selectors/sessionSelectors';

export const addAttendeeMutator: (sessionId: string, key: string) => void = mutatorAction(
    'addAttendeeMutator',
    (sessionId: string, key: string): void => {
        const attendees = getSessionAttendees(sessionId);

        if (!attendees.some(a => a == key)) {
            attendees.push(key);
        }

        if (!getFreeBusyCacheStore().attendeeMap.has(key)) {
            getFreeBusyCacheStore().attendeeMap.set(key, {
                sessions: [sessionId],
                freeBusyData: new Map<string, FreeBusyRow>(),
                workingHours: getUserConfiguration()?.UserOptions?.WorkingHours,
                flexibleWorkingHours: [],
            });
        } else {
            // Attendee is already in the cache, add this session to the list
            const sessions = getAttendeeSessions(key);

            if (!sessions.some(session => session == sessionId)) {
                sessions.push(sessionId);
            }
        }
    }
);

export const removeSessionFromAttendeeMutator: (key: string, sessionId: string) => void =
    mutatorAction('removeSessionFromAttendeeMutator', (key: string, sessionId: string): void => {
        const attendeeData = getAttendeeData(key);

        if (attendeeData) {
            attendeeData.sessions = attendeeData.sessions.filter(session => session != sessionId);

            if (attendeeData.sessions.length == 0) {
                // Clear cached free/busy data if there are no more
                // sessions requesting data for this attendee, but keep
                // the attendee in the list so that working hours are cached
                attendeeData.freeBusyData.clear();
                attendeeData.flexibleWorkingHours = [];
            }
        }
    });

export const setAttendeeWorkingHoursPropsMutator: (
    key: string,
    workingHours?: WorkingHoursType,
    flexibleWorkingHours?: GetScheduleFlexibleWorkingHoursView[]
) => void = mutatorAction(
    'setAttendeeWorkingHoursPropsMutator',
    (
        key: string,
        workingHours?: WorkingHoursType,
        flexibleWorkingHours?: GetScheduleFlexibleWorkingHoursView[]
    ): void => {
        const attendeeData = getAttendeeData(key);

        if (!attendeeData) {
            return;
        }

        if (workingHours) {
            attendeeData.workingHours = workingHours;
        }

        if (flexibleWorkingHours) {
            attendeeData.flexibleWorkingHours = attendeeData.flexibleWorkingHours
                ? mergeFlexibleWorkingHours(attendeeData.flexibleWorkingHours, flexibleWorkingHours)
                : flexibleWorkingHours;
        }
    }
);
